import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';

import { Course } from '@/features/course-progress/api/types';
import { ELEVATED_ROLES } from '@/lib/constants/auth.constants';
import { OneRosterApiTyped } from '@/lib/http.server';
import { assertUserHasRole } from '@/middlewares/userAuthMiddleware';
import { standardMiddlewares } from '@/middlewares/standardMiddlewares';

export const getCoursesDefaultParams = {
  filter: `status='active'`,
  sort: 'title',
  orderBy: 'asc',
  limit: '100',
  offset: '0',
  search: '',
};

type CoursesResponse = {
  courses: Course[];
  totalCount: number;
  pageCount: number;
  pageNumber: number;
  offset: number;
  limit: number;
};

const fetchOnerosterCourses = async (
  onerosterApi: OneRosterApiTyped,
  params: {
    filter?: string;
    sort?: string;
    limit?: string;
    offset?: string;
    orderBy?: string;
    search?: string;
  }
) => {
  const mergedParams = Object.assign({}, getCoursesDefaultParams, params);

  const response = await onerosterApi.GET(
    '/ims/oneroster/rostering/v1p2/courses/',
    {
      params: {
        query: {
          filter: mergedParams.filter,
          sort: mergedParams.sort,
          orderBy: (mergedParams.orderBy ?? 'asc') as 'asc' | 'desc',
          limit: parseInt(mergedParams.limit),
          offset: parseInt(mergedParams.offset),
          search: mergedParams.search ?? '',
        },
      },
    }
  );

  return response.data;
};

type GetCoursesResponse = {
  data: CoursesResponse | null;
  success: boolean;
  error: string | null;
};

export const getCourses = createServerFn({ method: 'POST' })
  .middleware([standardMiddlewares])
  .validator(
    z.object({
      filter: z.string().optional().default(getCoursesDefaultParams.filter),
      sort: z.string().optional().default(getCoursesDefaultParams.sort),
      limit: z.string().optional().default(getCoursesDefaultParams.limit),
      offset: z.string().optional().default(getCoursesDefaultParams.offset),
      search: z.string().optional().default(getCoursesDefaultParams.search),
    })
  )
  .handler(async ({ context: { cognitoUser, oneRosterApiTyped }, data }) => {
    await assertUserHasRole(cognitoUser, oneRosterApiTyped, ELEVATED_ROLES);
    const response = await fetchOnerosterCourses(oneRosterApiTyped, data);
    return {
      data: response,
      success: true,
      error: null,
    } as GetCoursesResponse;
  });
